import React from "react";
import { Helmet } from "react-helmet-async";

const DOMAIN = "https://pttnz.net";
const OG_IMAGE = `${DOMAIN}/assets/hero.jpg`;
const BRAND = "PTT NZ Limited";

// Used to auto-generate BreadcrumbList structured data from a path.
const PAGE_LABELS = {
  "/": "Home",
  "/about": "About",
  "/solutions": "Solutions",
  "/solutions/private-apn": "Private APN & Secure VPN",
  "/solutions/android-mdm": "Android MDM",
  "/solutions/byoc": "BYOC — Bring Your Own Carrier",
  "/solutions/enterprise-iot": "Enterprise & Industrial IoT",
  "/network-outage": "Network Outage Help",
  "/products": "Hardware",
  "/plans": "Plans",
  "/rv-motorhome": "RV & Motorhome",
  "/iot-data-pools": "IoT & Data Pools",
  "/resellers": "Resellers",
  "/contact": "Contact",
};

/**
 * Per-page SEO override. Places on top of the base tags in index.html.
 * @param {string} title      Page title (will be suffixed with brand)
 * @param {string} description Page description (~155 chars ideal)
 * @param {string} keywords   Optional comma-separated page-specific keywords
 * @param {string} path       Page path e.g. "/products" — used for canonical + og:url + breadcrumb
 * @param {object|object[]} jsonLd Optional JSON-LD structured data (object or array of objects)
 */
const SEO = ({ title, description, keywords, path = "/", jsonLd, noindex = false }) => {
  const fullTitle = title ? `${title} · ${BRAND}` : "Multi-Network Cellular SIMs & eSIMs for New Zealand & Australia · PTT NZ Limited";
  const url = `${DOMAIN}${path === "/" ? "" : path}`;
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  if (path !== "/") {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: DOMAIN + "/" },
        { "@type": "ListItem", position: 2, name: PAGE_LABELS[path] || title, item: url },
      ],
    });
  }

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={BRAND} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:locale" content="en_NZ" />
      <meta property="og:locale:alternate" content="en_AU" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={OG_IMAGE} />

      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(schema)}</script>
      ))}
    </Helmet>
  );
};

export default SEO;
